/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { isNotNil } from '@common/tech-shared';
import { isNil } from 'lodash-es';
import {
  ErrorRepresentation,
  KEYCLOAK_VALIDATION_ERROR_STATUS_CODES,
  KeycloakErrorResponseData,
  KeycloakHttpErrorResponse,
} from './keycloak-error.model';

export function extractErrorRepresentations(error: KeycloakHttpErrorResponse): ErrorRepresentation[] {
  if (!canHandleKeycloakError(error)) {
    throw new Error(`Cannot handle keycloak error: ${error}`);
  }
  const errorResponseData: KeycloakErrorResponseData = error.responseData;
  return error.responseData.errors ?? [errorResponseData];
}

export function canHandleKeycloakError(err: KeycloakHttpErrorResponse): boolean {
  if (!KEYCLOAK_VALIDATION_ERROR_STATUS_CODES.includes(err?.response?.status)) return false;
  if (isNil(err?.responseData)) return false;

  if (containsErrorMessage(err)) return true;
  return containsErrorArray(err) && isNotNil(err.responseData.errors[0]?.errorMessage);
}

function containsErrorMessage(err: any): boolean {
  return isNotNil(err?.responseData?.errorMessage);
}

function containsErrorArray(err: any): boolean {
  return isNotNil(err?.responseData?.errors) && err.responseData.errors.length > 0;
}

export function isSingleErrorMessage(err: KeycloakErrorResponseData): boolean {
  return isNotNil(err.errorMessage) && isNil(err.field) && isNil(err.params) && isNil(err.errors);
}

export function isFieldErrorMessage(err: KeycloakErrorResponseData): boolean {
  return isNotNil(err.errorMessage) && isNotNil(err.field) && isNotNil(err.params) && isNil(err.errors);
}
